/**
 * TanStack Query hooks para /v1/actividades — FWA-PWA-030.
 *
 * Wire automático a outbox offline (heredado pattern PWA-029):
 * - navigator.onLine === false → addOutboxItem antes de fetch
 * - catch TypeError fetch → addOutboxItem fallback
 * - Idempotency-Key preservado en headers
 */
import type {
  Actividad,
  ActividadFullCreatePayload,
  ActividadUpdatePayload,
  ActividadWithSubtype,
} from '@sisrep/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse, apiGet, apiPatch, apiPost, generateIdempotencyKey } from '../api.js';
import { addOutboxItem } from '../outbox/db.js';

// ─── Query keys ───────────────────────────────────────────────────────────

export const actividadesKeys = {
  all: ['actividades'] as const,
  list: (filters: ActividadesListFilters) => ['actividades', 'list', filters] as const,
  detail: (id: string) => ['actividades', 'detail', id] as const,
};

// ─── List + detail ────────────────────────────────────────────────────────

export interface ActividadesListFilters {
  cursor?: string | undefined;
  limit?: number | undefined;
  rama?: string | undefined;
  tipo?: string | undefined;
  quarter?: string | undefined;
}

interface ActividadesListResponse {
  data: Actividad[];
  next_cursor: string | null;
  has_more: boolean;
}

function buildListQueryString(filters: ActividadesListFilters): string {
  const params = new URLSearchParams();
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.rama) params.set('rama', filters.rama);
  if (filters.tipo) params.set('tipo', filters.tipo);
  if (filters.quarter) params.set('quarter', filters.quarter);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useActividadesList(filters: ActividadesListFilters = {}) {
  return useQuery<ApiResponse<ActividadesListResponse>>({
    queryKey: actividadesKeys.list(filters),
    queryFn: () =>
      apiGet<ActividadesListResponse>(`/v1/actividades${buildListQueryString(filters)}`),
  });
}

export function useActividad(id: string | undefined) {
  return useQuery<ApiResponse<ActividadWithSubtype>>({
    queryKey: actividadesKeys.detail(id ?? ''),
    queryFn: () => apiGet<ActividadWithSubtype>(`/v1/actividades/${id}`),
    enabled: Boolean(id),
  });
}

// ─── Outbox-aware mutations ───────────────────────────────────────────────

function isOfflineNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err instanceof TypeError && err.message.toLowerCase().includes('fetch')) return true;
  return false;
}

export function useCreateActividad() {
  const qc = useQueryClient();
  return useMutation<
    ApiResponse<ActividadWithSubtype> | { queuedOffline: true },
    Error,
    ActividadFullCreatePayload
  >({
    mutationKey: ['actividades', 'create'],
    mutationFn: async (payload) => {
      const idempotencyKey = generateIdempotencyKey();
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await addOutboxItem({
            endpoint: '/v1/actividades',
            method: 'POST',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey },
          });
          return { queuedOffline: true };
        }
        return await apiPost<ActividadWithSubtype, ActividadFullCreatePayload>(
          '/v1/actividades',
          payload,
          { idempotencyKey },
        );
      } catch (err) {
        if (isOfflineNetworkError(err)) {
          await addOutboxItem({
            endpoint: '/v1/actividades',
            method: 'POST',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey },
          });
          return { queuedOffline: true };
        }
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actividadesKeys.all });
      void qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

export interface UpdateActividadArgs {
  id: string;
  payload: ActividadUpdatePayload;
  etag: string;
}

export function useUpdateActividad() {
  const qc = useQueryClient();
  return useMutation<ApiResponse<Actividad> | { queuedOffline: true }, Error, UpdateActividadArgs>({
    mutationKey: ['actividades', 'update'],
    mutationFn: async ({ id, payload, etag }) => {
      const idempotencyKey = generateIdempotencyKey();
      const endpoint = `/v1/actividades/${id}`;
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await addOutboxItem({
            endpoint,
            method: 'PATCH',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey, 'If-Match': `"${etag}"` },
          });
          return { queuedOffline: true };
        }
        return await apiPatch<Actividad, ActividadUpdatePayload>(endpoint, payload, {
          idempotencyKey,
          ifMatch: etag,
        });
      } catch (err) {
        if (isOfflineNetworkError(err)) {
          await addOutboxItem({
            endpoint,
            method: 'PATCH',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey, 'If-Match': `"${etag}"` },
          });
          return { queuedOffline: true };
        }
        throw err;
      }
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: actividadesKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: actividadesKeys.all });
      void qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}
